import type {
  CalendarEvent,
  DeleteEventRequest,
  EventBriefsResponse,
  RecurringSeriesResponse,
  RsvpEventRequest,
  UpdateEventRequest,
} from "@/types/events.ts";
import { queryOptions, skipToken } from "@tanstack/react-query";
import { api } from "@/lib/http.ts";

export type Calendar = {
  id: number;
  accountId: number;
  connectorId?: number;
  /**
   * Owning connector's provider ("google" | "microsoft" | "device" | …), or
   * null for connectorless built-ins (okthx reminders). Optional so a response
   * from a backend that predates this field still type-checks — both absent
   * (`undefined`) and `null` evaluate as unsupported when a caller gates on a
   * specific provider (e.g. `calendar?.provider === "google"`).
   */
  provider?: string;
  calendarId: string;
  summary: string | null;
  description: string | null;
  timeZone: string | null;
  accessRole: string | null;
  primary: boolean;
  color: string | null;
  updatedAt: string;
};

export const getCalendars = async (): Promise<Calendar[]> => {
  const resp = await api.get<Calendar[]>("/api/a/calendars");
  return resp.data;
};

/**
 * Set the user-changeable color of a calendar. Only the okthx (reminders)
 * calendar accepts this — the server rejects any other calendarId.
 */
export const setCalendarColor = async (
  calendarId: string,
  color: string,
): Promise<{ calendarId: string; color: string }> => {
  const resp = await api.patch<{ calendarId: string; color: string }>(
    `/api/a/calendars/${encodeURIComponent(calendarId)}/color`,
    { color },
  );
  return resp.data;
};

/**
 * The user's calendars. Keyed exactly as the older inline `useQuery` call sites
 * key it, so the two share one cache entry and an invalidation from either
 * reaches both — a factory on its own key would quietly fork the cache.
 */
export const calendarQueries = {
  list: () =>
    queryOptions({
      queryKey: ["calendars"] as const,
      queryFn: async ({ signal }) => {
        const resp = await api.get<Calendar[]>("/api/a/calendars", { signal });
        return resp.data;
      },
    }),
};

export const eventQueries = {
  frequentRecurringSeries: () =>
    queryOptions({
      queryKey: ["frequent-recurring-series"] as const,
      queryFn: async ({ signal }) => {
        const response = await api.get<RecurringSeriesResponse>(
          "/api/a/events/frequent-recurring-series",
          { signal },
        );
        return response.data;
      },
    }),
  detail: (eventId: string) =>
    queryOptions({
      queryKey: ["event", eventId] as const,
      queryFn: async ({ signal }) => {
        const resp = await api.get<CalendarEvent>(`/api/a/events/${eventId}`, { signal });
        return resp.data;
      },
      staleTime: 60_000,
    }),
  /**
   * Briefs the assistant pinned to an event. Fetched separately from the event
   * because the detail panel usually serves the event itself out of the
   * calendar-list cache, which never carries this.
   */
  briefs: (eventId: string | undefined) =>
    queryOptions({
      queryKey: ["event", eventId, "briefs"] as const,
      queryFn: eventId
        ? async ({ signal }) => {
            const resp = await api.get<EventBriefsResponse>(`/api/a/events/${eventId}/briefs`, {
              signal,
            });
            return resp.data.briefs;
          }
        : skipToken,
      staleTime: 60_000,
    }),
};

export const updateEvent = async (
  data: UpdateEventRequest,
): Promise<{ ok: boolean; event?: CalendarEvent }> => {
  const resp = await api.post<{ ok: boolean; event?: CalendarEvent }>("/api/a/events/update", data);
  return resp.data;
};

export const rsvpEvent = async (
  data: RsvpEventRequest,
): Promise<{ ok: boolean; event?: CalendarEvent }> => {
  const resp = await api.post<{ ok: boolean; event?: CalendarEvent }>("/api/a/events/rsvp", data);
  return resp.data;
};

export const deleteEvent = async (data: DeleteEventRequest): Promise<{ ok: boolean }> => {
  const resp = await api.post<{ ok: boolean }>("/api/a/events/delete", data);
  return resp.data;
};
